/**
 * Estate Flow - Tax & P&L Management (Admin)
 * Global tax jurisdiction management and reporting overview
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Calculator,
  FileText,
  Globe,
  Building2,
  DollarSign,
  Calendar,
  Download,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Info,
  ChevronRight
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Country flag emojis
const FLAGS = {
  'BS': '🇧🇸', 'US': '🇺🇸', 'US-FL': '🇺🇸', 'GB': '🇬🇧', 'CA': '🇨🇦',
  'EU': '🇪🇺', 'JM': '🇯🇲', 'TT': '🇹🇹', 'BB': '🇧🇧', 'KY': '🇰🇾',
  'DE': '🇩🇪', 'FR': '🇫🇷', 'IT': '🇮🇹', 'ES': '🇪🇸', 'NL': '🇳🇱'
};

const TaxManagement = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [jurisdictions, setJurisdictions] = useState({});
  const [usStates, setUsStates] = useState({});
  const [summary, setSummary] = useState(null);
  const [allConfigs, setAllConfigs] = useState([]);
  const [selectedJurisdiction, setSelectedJurisdiction] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('access_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();

      // Fetch jurisdictions, summary, and configs in parallel
      const [jurisdictionsRes, summaryRes, configsRes] = await Promise.all([
        axios.get(`${API}/tax/jurisdictions`, { headers }),
        axios.get(`${API}/tax/admin/summary`, { headers }),
        axios.get(`${API}/tax/admin/all-configs`, { headers })
      ]);

      setJurisdictions(jurisdictionsRes.data.jurisdictions || {});
      setUsStates(jurisdictionsRes.data.us_states || {});
      setSummary(summaryRes.data);
      setAllConfigs(configsRes.data.configurations || []);
    } catch (error) {
      console.error('Error fetching tax data:', error);
      toast.error('Failed to load tax data');
    } finally {
      setLoading(false);
    }
  };

  const getTaxStatusBadge = (status) => {
    if (status === 'yes') {
      return <Badge className="bg-emerald-100 text-emerald-700">Tax Enabled</Badge>;
    } else if (status === 'partial') {
      return <Badge className="bg-amber-100 text-amber-700">Partial</Badge>;
    }
    return <Badge variant="secondary">Not Applicable</Badge>;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <Calculator className="h-6 w-6 mr-2 text-emerald-600" />
            Tax & P&L Management
          </h2>
          <p className="text-gray-600 mt-1">
            Multi-country tax compliance • VAT • Sales Tax • GST/HST
          </p>
        </div>
        <Button onClick={fetchData} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="jurisdictions">Tax Jurisdictions</TabsTrigger>
          <TabsTrigger value="organizations">Organization Configs</TabsTrigger>
          <TabsTrigger value="us-states">US State Rates</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Organizations</p>
                    <p className="text-3xl font-bold text-gray-900">
                      {summary?.summary?.total_organizations || 0}
                    </p>
                  </div>
                  <Building2 className="h-10 w-10 text-blue-500 opacity-50" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Tax Enabled</p>
                    <p className="text-3xl font-bold text-emerald-600">
                      {summary?.summary?.tax_enabled || 0}
                    </p>
                  </div>
                  <CheckCircle className="h-10 w-10 text-emerald-500 opacity-50" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Tax Disabled</p>
                    <p className="text-3xl font-bold text-gray-600">
                      {summary?.summary?.tax_disabled || 0}
                    </p>
                  </div>
                  <XCircle className="h-10 w-10 text-gray-400 opacity-50" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Jurisdictions</p>
                    <p className="text-3xl font-bold text-purple-600">
                      {Object.keys(jurisdictions).length}
                    </p>
                  </div>
                  <Globe className="h-10 w-10 text-purple-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* By Jurisdiction */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Globe className="h-5 w-5 mr-2" />
                Organizations by Jurisdiction
              </CardTitle>
            </CardHeader>
            <CardContent>
              {summary?.by_jurisdiction && Object.keys(summary.by_jurisdiction).length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {Object.entries(summary.by_jurisdiction).map(([code, count]) => (
                    <div key={code} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center">
                        <span className="text-xl mr-2">{FLAGS[code] || '🌍'}</span>
                        <span className="font-medium">{jurisdictions[code]?.name || code}</span>
                      </div>
                      <Badge>{count}</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No organizations have tax enabled yet</p>
              )}
            </CardContent>
          </Card>

          {/* Recent Reports */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <FileText className="h-5 w-5 mr-2" />
                Recent Tax Reports
              </CardTitle>
            </CardHeader>
            <CardContent>
              {summary?.recent_reports?.length > 0 ? (
                <div className="space-y-2">
                  {summary.recent_reports.map((report, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                      <div className="flex items-center">
                        <FileText className="h-4 w-4 mr-3 text-gray-400" />
                        <div>
                          <p className="font-medium">{report.report_id}</p>
                          <p className="text-sm text-gray-500">
                            {report.report_type === 'quarterly_tax' ? 'Quarterly Tax' : 'P&L Report'}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">
                          {new Date(report.generated_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No reports generated yet</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Jurisdictions Tab */}
        <TabsContent value="jurisdictions" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Supported Tax Jurisdictions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(jurisdictions).map(([code, info]) => (
                  <div
                    key={code}
                    className="border rounded-lg p-4 hover:border-emerald-500 cursor-pointer transition-colors"
                    onClick={() => setSelectedJurisdiction({ code, ...info })}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center">
                        <span className="text-2xl mr-3">{FLAGS[code] || '🌍'}</span>
                        <div>
                          <h3 className="font-semibold">{info.name}</h3>
                          <p className="text-sm text-gray-500">{info.tax_type}</p>
                        </div>
                      </div>
                      <Badge variant="outline" className="text-lg">
                        {info.standard_rate || info.gst_rate || 0}%
                      </Badge>
                    </div>

                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Filing Frequency:</span>
                        <span className="capitalize">{info.filing_frequency}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Form:</span>
                        <span>{info.form_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Currency:</span>
                        <span>{info.currency}</span>
                      </div>
                    </div>

                    <div className="mt-3 pt-3 border-t">
                      <p className="text-xs text-gray-600">{info.notes}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Organization Configs Tab */}
        <TabsContent value="organizations" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Organization Tax Configurations</CardTitle>
            </CardHeader>
            <CardContent>
              {allConfigs.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3">Organization</th>
                        <th className="text-left p-3">Status</th>
                        <th className="text-left p-3">Jurisdiction</th>
                        <th className="text-left p-3">Tax ID</th>
                        <th className="text-left p-3">Filing</th>
                        <th className="text-left p-3">Updated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allConfigs.map((config, idx) => (
                        <tr key={idx} className="border-b hover:bg-gray-50">
                          <td className="p-3 font-medium">{config.org_name}</td>
                          <td className="p-3">{getTaxStatusBadge(config.tax_applicable)}</td>
                          <td className="p-3">
                            <span className="mr-1">{FLAGS[config.country_code] || '🌍'}</span>
                            {jurisdictions[config.country_code]?.name || config.country_code}
                            {config.state_code && ` (${config.state_code})`}
                          </td>
                          <td className="p-3 font-mono text-sm">
                            {config.tax_registration_number || '-'}
                          </td>
                          <td className="p-3 capitalize">{config.filing_frequency}</td>
                          <td className="p-3 text-sm text-gray-500">
                            {config.updated_at ? new Date(config.updated_at).toLocaleDateString() : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No organizations have configured tax settings yet</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* US States Tab */}
        <TabsContent value="us-states" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <span className="mr-2">🇺🇸</span>
                US State Sales Tax Rates
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {Object.entries(usStates)
                  .sort((a, b) => a[1].name.localeCompare(b[1].name))
                  .map(([code, info]) => (
                    <div key={code} className="border rounded-lg p-3 hover:border-emerald-500">
                      <div className="flex justify-between items-center">
                        <span className="font-medium">{info.name}</span>
                        <Badge variant="outline">{code}</Badge>
                      </div>
                      <div className="mt-2 space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-500">State Rate:</span>
                          <span className={info.state_rate === 0 ? 'text-emerald-600' : ''}>
                            {info.state_rate}%
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Avg Local:</span>
                          <span>{info.avg_local}%</span>
                        </div>
                        <div className="flex justify-between font-medium border-t pt-1">
                          <span>Combined:</span>
                          <span className="text-emerald-600">
                            {(info.state_rate + info.avg_local).toFixed(2)}%
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Jurisdiction Detail Dialog */}
      <Dialog open={!!selectedJurisdiction} onOpenChange={() => setSelectedJurisdiction(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <span className="text-2xl mr-2">{FLAGS[selectedJurisdiction?.code] || '🌍'}</span>
              {selectedJurisdiction?.name} - Tax Details
            </DialogTitle>
          </DialogHeader>

          {selectedJurisdiction && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <Label className="text-gray-500">Tax Type</Label>
                  <p className="text-xl font-semibold">{selectedJurisdiction.tax_type}</p>
                </div>
                <div className="bg-emerald-50 p-4 rounded-lg">
                  <Label className="text-gray-500">Standard Rate</Label>
                  <p className="text-xl font-semibold text-emerald-600">
                    {selectedJurisdiction.standard_rate || selectedJurisdiction.gst_rate || 0}%
                  </p>
                </div>
              </div>

              <div className="border rounded-lg p-4">
                <h4 className="font-semibold mb-3">Filing Information</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <Label className="text-gray-500">Form Name</Label>
                    <p>{selectedJurisdiction.form_name}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Filing Frequency</Label>
                    <p className="capitalize">{selectedJurisdiction.filing_frequency}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Deadline</Label>
                    <p>{selectedJurisdiction.deadline_days} days after period end</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Registration Threshold</Label>
                    <p>
                      {selectedJurisdiction.registration_threshold
                        ? `${selectedJurisdiction.currency} ${selectedJurisdiction.registration_threshold.toLocaleString()}`
                        : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {selectedJurisdiction.categories && (
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-3">Tax Categories</h4>
                  <div className="space-y-2">
                    {Object.entries(selectedJurisdiction.categories).map(([cat, info]) => (
                      <div key={cat} className="flex items-center justify-between py-2 border-b last:border-0">
                        <div>
                          <span className="capitalize">{cat.replace(/_/g, ' ')}</span>
                          {info.note && (
                            <p className="text-xs text-gray-500">{info.note}</p>
                          )}
                        </div>
                        <div className="flex items-center">
                          {info.exempt ? (
                            <Badge className="bg-gray-100 text-gray-600">Exempt</Badge>
                          ) : (
                            <Badge className="bg-emerald-100 text-emerald-700">{info.rate}%</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-start">
                  <Info className="h-5 w-5 text-blue-500 mr-2 mt-0.5" />
                  <p className="text-sm text-blue-800">{selectedJurisdiction.notes}</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TaxManagement;
